import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TransactionHistoryTable from './TransactionHistoryTable';
import ElinxHistoryTable from './ElinxHistoryTable';
import BankVerifyHistoryTable from './BankVerifyHistoryTable';
import ScheduledTransactionHistoryTable from './ScheduledTransactionHistoryTable';
import ScheduledClientAccountTransfersTable from './ScheduledClientAccountTransfersTable';
import { ActivityTabType } from './ActivityType';

export type ActivityTypeVisibility = Partial<Record<ActivityTabType, boolean>>;

// The filter params the sub-tables keep in the URL (portal mode) — cleared
// when the user switches activity type, exactly like the portal's ActivityTab.
const URL_FILTER_PARAMS = [
  'startDate',
  'endDate',
  'status',
  'flagged',
  'type',
  'currency',
  'searchTerm',
  'timeZone',
  'eLinxType',
  'verificationStatus',
  'scheduleType',
  'transactionType',
  'frequency',
];

// Before ID-12976 the activity types were their own display strings, so URLs
// held outside the app (bookmarks, shared links, backend-built links) still
// carry e.g. ?activityType=Bank%20Verify%20History. Map those to the current
// keys so an old deep link opens the type it names instead of silently
// falling back to the first one.
const LEGACY_ACTIVITY_TYPES: Partial<Record<string, ActivityTabType>> = {
  'Transaction History': 'transactionHistory',
  'eLinx History': 'eLinxHistory',
  'Bank Verify History': 'bankVerifyHistory',
  'Scheduled Transactions': 'scheduledTransactions',
  'Scheduled Client Account Transfers': 'scheduledClientAccountTransfers',
};

type ContactActivityTabProps = {
  // The linked record the history is scoped to.
  clientAccountId: string;
  clientAccountsEnabled: boolean;
  country: string;
  // Inactive records can't remove a transaction's fraud flag (portal parity).
  active: boolean;
  // Per-type gating for hosts with feature flags (the portal). Omitted types
  // default to visible.
  activityTypes?: ActivityTypeVisibility;
  // Portal pages keep the activity type and filters in the page URL (deep
  // links, reload/back restoration); the drawer keeps them in memory — it
  // must not rewrite the host URL.
  persistFiltersInUrl?: boolean;
  // The user's preferred timezone for the history tables (default UTC).
  timezone?: string;
  // Renders the portal's Include Sub Accounts checkbox in the transaction
  // filter bar (visible-but-inert in this tab, as on the portal pages).
  showIncludeSubAccounts?: boolean;
};

// The portal's Customer Activity tab: the same sub-tables behind one Activity
// Type selector, host-configurable for the drawer (in-memory filters, no
// feature flags) and the portal pages (URL-persisted filters, flag gating).
const ContactActivityTab: FunctionComponent<ContactActivityTabProps> = ({
  clientAccountId,
  clientAccountsEnabled,
  country,
  active,
  activityTypes,
  persistFiltersInUrl = false,
  timezone,
  showIncludeSubAccounts = false,
}) => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Contacts.Activity' });

  const activityTypeOptions = useMemo(() => {
    const options: { label: string; value: ActivityTabType; visible: boolean }[] = [
      {
        label: t('transactionHistory', { defaultValue: 'Transaction History' }),
        value: 'transactionHistory',
        visible: activityTypes?.['transactionHistory'] ?? true,
      },
      {
        label: t('elinxHistory', { defaultValue: 'eLinx History' }),
        value: 'eLinxHistory',
        visible: activityTypes?.['eLinxHistory'] ?? true,
      },
      {
        label: t('bankVerifyHistory', { defaultValue: 'Bank Verify History' }),
        value: 'bankVerifyHistory',
        visible: activityTypes?.['bankVerifyHistory'] ?? true,
      },
      {
        label: t('scheduledTransactions', { defaultValue: 'Scheduled Transactions' }),
        value: 'scheduledTransactions',
        visible: activityTypes?.['scheduledTransactions'] ?? true,
      },
      {
        label: t('scheduledClientAccountTransfers', { defaultValue: 'Scheduled Client Account Transfers' }),
        value: 'scheduledClientAccountTransfers',
        visible: (activityTypes?.['scheduledClientAccountTransfers'] ?? true) && clientAccountsEnabled,
      },
    ];

    return options.filter((option) => option.visible);
  }, [clientAccountsEnabled, activityTypes, t]);

  // Initial type: the URL's ?activityType= in portal mode (when it names a
  // visible option), else the first visible option — never a type the host's
  // flags hide.
  const [currentType, setCurrentType] = useState<ActivityTabType>(() => {
    const urlValue = persistFiltersInUrl ? new URLSearchParams(window.location.search).get('activityType') : null;
    const fromUrl = urlValue ? (LEGACY_ACTIVITY_TYPES[urlValue] ?? urlValue) : null;

    return (
      activityTypeOptions.find((option) => option.value === fromUrl)?.value ??
      activityTypeOptions[0]?.value ??
      'transactionHistory'
    );
  });

  const handleCurrentType = (type: ActivityTabType) => {
    setCurrentType(type);

    if (!persistFiltersInUrl) return;

    // Each sub-table owns different filter params; drop them all so the next
    // table starts clean (portal behavior).
    const searchParams = new URLSearchParams(window.location.search);
    URL_FILTER_PARAMS.forEach((param) => searchParams.delete(param));
    history.replace({ search: searchParams.toString() });
  };

  // Leaving the tab clears the whole query string (portal behavior) so other
  // tabs don't inherit stale filter params — but only while still on the same
  // page. When the unmount is caused by a link-out navigation, the URL already
  // points at the target page and a replace here would strip the router state
  // the link-out just pushed (e.g. bank-verify details render from it).
  useEffect(() => {
    if (!persistFiltersInUrl) return undefined;

    const mountPathname = window.location.pathname;

    return () => {
      if (window.location.pathname === mountPathname) {
        history.replace(mountPathname);
      }
    };
  }, []);

  const subTableProps = {
    clientAccountId,
    clientAccountsEnabled,
    country,
    activityTypeOptions,
    currentType,
    handleCurrentType,
    persistFiltersInUrl,
    timezone,
  };

  return (
    <>
      {currentType === 'transactionHistory' && (
        <TransactionHistoryTable {...subTableProps} active={active} showIncludeSubAccounts={showIncludeSubAccounts} />
      )}

      {currentType === 'eLinxHistory' && <ElinxHistoryTable {...subTableProps} />}
      {currentType === 'bankVerifyHistory' && <BankVerifyHistoryTable {...subTableProps} />}
      {currentType === 'scheduledTransactions' && <ScheduledTransactionHistoryTable {...subTableProps} />}
      {currentType === 'scheduledClientAccountTransfers' && <ScheduledClientAccountTransfersTable {...subTableProps} />}
    </>
  );
};

export default ContactActivityTab;
